/**
 * RunStepper — the legible pipeline on sheet 13 (callouts 3 and 4; mission 6
 * WP07 T040; FR-011, FR-027; GEN.4, GEN.5, GEN.7, U.2; research §1.17;
 * post-plan C10, T1; post-tasks E18).
 *
 * Drawn from the dispatcher's polled `run` alone; it never fetches the run.
 * - Path A: Validation · Generation · Analysis · Ready. Path B: Pre-check ·
 *   Analysis · Ready. `validating` is the first node; queued and any state the
 *   SPA does not know are all upcoming (C10). Validation and the pre-check ran
 *   at upload completion, so they read done once the run generates or analyzes.
 * - A failed run keeps the stepper only when the failure names a stage
 *   (generation-empty, analysis-timeout); other failures and a cancel render
 *   nothing, and InProgress shows the state panel instead.
 * - While generating or analyzing, the §7.10 bar sits under the stepper: the
 *   stage name and the counts in mono (`progress × record_count` of
 *   `record_count`), the elapsed time from `started_at`, and the GEN.7
 *   typical-time sentence at the served admission cap (13/4, C-004).
 * - One `aria-live="polite"` region holds the node words, the stage and the
 *   counts; the ticking clock sits outside it so it is not read every second.
 */
import { useQuery } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { cn } from '@/components/ui/cn'
import { Progress } from '@/components/ui/progress'
import type { Run } from '@/lib/api/runs'
import { Mono, count } from '@/lib/format'
import { PROVISIONAL_COPY, copyText, type TextKey } from '@/lib/provisional-copy'
import { layoutQuery } from '@/lib/queries'

export type NodeState = 'done' | 'current' | 'upcoming' | 'failed'

type StageKey = 'stage.validation' | 'stage.preCheck' | 'stage.generation' | 'stage.analysis' | 'stage.ready'

const PATH_STAGES: Record<Run['path'], readonly StageKey[]> = {
  generate: ['stage.validation', 'stage.generation', 'stage.analysis', 'stage.ready'],
  analyze: ['stage.preCheck', 'stage.analysis', 'stage.ready'],
}

/**
 * The node state words, visually hidden beside each node. They are the state
 * names style guide §7.9 gives (completed, current, upcoming, failed); register
 * 7 carries no entry for them (copy request for WP13 in the WP07 handoff).
 */
const STATE_WORD: Record<NodeState, string> = {
  done: 'completed',
  current: 'current',
  upcoming: 'upcoming',
  failed: 'failed',
}

/** The step 1 table of the WP07 prompt; null means the state panel replaces the stepper. */
export function nodeStates(run: Pick<Run, 'path' | 'state' | 'failure_reason'>): NodeState[] | null {
  const pathA = run.path === 'generate'
  const n = pathA ? 4 : 3
  const row = (...head: NodeState[]): NodeState[] => [...head, ...Array<NodeState>(n - head.length).fill('upcoming')]
  switch (run.state) {
    case 'validating':
      return row('current')
    case 'generating':
      return pathA ? row('done', 'current') : row()
    case 'analyzing':
      return pathA ? row('done', 'done', 'current') : row('done', 'current')
    case 'failed':
      if (run.failure_reason === 'analysis-timeout') return pathA ? row('done', 'done', 'failed') : row('done', 'failed')
      if (run.failure_reason === 'generation-empty' && pathA) return row('done', 'failed')
      return null
    case 'cancelled':
      return null
    default:
      // queued, and any state the SPA does not know yet (C10).
      return row()
  }
}

/** `mm:ss`, or `h:mm:ss` from one hour; a number format, not copy. */
function elapsed(ms: number): { text: string; iso: string } {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (v: number) => String(v).padStart(2, '0')
  const text = h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
  const iso = total === 0 ? 'PT0S' : `PT${h > 0 ? `${h}H` : ''}${m > 0 ? `${m}M` : ''}${s > 0 ? `${s}S` : ''}`
  return { text, iso }
}

/** One interval that updates local state only; cleared on unmount, which a terminal state causes. */
function Elapsed({ startedAt }: { startedAt: string }) {
  const start = Date.parse(startedAt)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const { text, iso } = elapsed(now - start)
  return (
    <time dateTime={iso} className="shrink-0 text-caption text-navy-600">
      <Mono>{text}</Mono>
    </time>
  )
}

/** GEN.7 at the cap, from `['layout','v1']` `limits.admission_cap` (C-004); omitted on a failed read. */
function TypicalTime() {
  const layout = useQuery(layoutQuery('v1'))
  if (!layout.data) return null
  return (
    <p className="mt-2 text-caption text-navy-600">
      {PROVISIONAL_COPY.typicalTimeAtCap.render({ admissionCap: count(layout.data.limits.admission_cap) })}
    </p>
  )
}

function NodeDot({ state }: { state: NodeState }) {
  switch (state) {
    case 'done':
      return (
        <span data-node-dot="" className="flex size-6 items-center justify-center rounded-full bg-navy-950 text-white">
          <Check aria-hidden className="size-3.5" />
        </span>
      )
    case 'current':
      return (
        <span
          data-node-dot=""
          className="flex size-6 items-center justify-center rounded-full bg-white [box-shadow:0_0_0_2px_var(--color-accent)]"
        >
          <span aria-hidden className="size-2 rounded-full bg-[var(--color-accent)]" />
        </span>
      )
    case 'failed':
      return (
        <span
          data-node-dot=""
          className="flex size-6 items-center justify-center rounded-full bg-[var(--color-error-600)] text-white"
        >
          <X aria-hidden className="size-3.5" />
        </span>
      )
    default:
      return <span data-node-dot="" className="block size-6 rounded-full border-[1.5px] border-navy-300 bg-white" />
  }
}

export interface RunStepperProps {
  run: Run
}

export function RunStepper({ run }: RunStepperProps) {
  const states = nodeStates(run)
  if (states === null) return null
  const stages = PATH_STAGES[run.path] ?? PATH_STAGES.generate
  const currentIndex = states.indexOf('current')
  const running = (run.state === 'generating' || run.state === 'analyzing') && currentIndex >= 0
  const stageName = currentIndex >= 0 ? copyText(stages[currentIndex] as TextKey) : ''
  const done = run.progress === null ? null : Math.round(run.progress * run.record_count)

  return (
    <section data-run-stepper="" className="flex flex-col gap-3">
      <div aria-live="polite" aria-atomic="false">
        <ol className="flex items-start">
          {stages.map((key, i) => {
            const state = states[i] ?? 'upcoming'
            const last = i === stages.length - 1
            return (
              <li
                key={key}
                data-node-state={state}
                aria-current={state === 'current' ? 'step' : undefined}
                className={cn('flex items-start', !last && 'flex-1')}
              >
                <div className="flex flex-col items-center gap-1.5">
                  <NodeDot state={state} />
                  <span
                    data-node-label=""
                    className={cn(
                      'text-[13px] font-medium leading-[18px]',
                      state === 'current' ? 'text-navy-950' : 'text-navy-600',
                    )}
                  >
                    {copyText(key)}
                  </span>
                  <span className="sr-only">{STATE_WORD[state]}</span>
                </div>
                {last ? null : (
                  <span
                    aria-hidden
                    data-connector=""
                    className={cn(
                      'mx-2 mt-[11px] h-0 flex-1 border-t-2',
                      state === 'done' ? 'border-navy-950' : 'border-navy-200',
                    )}
                  />
                )}
              </li>
            )
          })}
        </ol>
        {running ? (
          <div className="mt-4 flex items-baseline justify-between gap-3">
            <span className="text-[13px] font-medium leading-[18px] text-navy-950">{stageName}</span>
            {done === null ? null : (
              <span className="text-caption text-navy-600">
                <Mono>
                  {count(done)} / {count(run.record_count)}
                </Mono>{' '}
                {/* The unit word as style guide §7.10 draws it ("4,120 / 9,012 records"); Path B counts base
                    segments (owner decision 2026-09-26, decision 6). */}
                {run.path === 'analyze' ? 'base segments' : 'records'}
              </span>
            )}
          </div>
        ) : null}
      </div>
      {running ? (
        <div>
          <div className="flex items-center gap-3">
            <Progress
              value={run.progress === null ? null : run.progress * 100}
              aria-label={stageName}
              className="flex-1"
            />
            <Elapsed startedAt={run.started_at} />
          </div>
          {/* GEN.7 is a generation time: not stated on Path B, as the estimate panel omits it (owner decision 2026-09-26). */}
          {run.path === 'analyze' ? null : <TypicalTime />}
        </div>
      ) : null}
    </section>
  )
}
